import { STEPS } from '../storyScript';

/* Hero layout: the scene artboards, the viewport tiers, and the stage
   geometry that Buffy, the cards and the composer share.

   Owner direction (2026-09-29, all binding):
     - Plain white hero. Buffy stands beside the product's own composer (the
       Assignments chat look); his line is plain text directly above it,
       left-aligned with the placeholder; the story's cards sit above that.
     - Buffy never leaves HOME (no jumps, no rises, no ledge).
     - The composer is big and fixed in size (64 px tall with 18 px text on
       desktop, 56 px with 16 px on a phone), so it is NOT scaled with the
       cards: it lives in the BAND under the artboard, in stage px.
     - One stage grid (lead, H4): on desktop the composer is centered on the
       page axis with Buffy outside it on the left, and every step's content
       is centered on that same axis (two-card steps balanced around it).

   Stage (stage px, top to bottom):
     ARTBOARD  the cards, authored on an artboard (desktop 860 x 272, phone
               343 x 192) and scaled by k to fit; centered in the stage.
     gap       16 px desktop, 12 px phone (Buffy's line is at least 16 px
               clear of every card).
     BAND      Buffy's line (1 to 4 lines) + 12 px + the composer. Buffy
               stands on the band's bottom line, left of the composer.
     progress  the step name, the five segments and the pause button.

   Buffy is never scaled with the cards: he is drawn at a whole number of
   DEVICE px per art pixel (buffyScale), his cell sits on the device-pixel
   grid (cellAt + buffy/pixelSnap.js), so his pixels stay exact at every size
   and every display scaling.

   Text tiers follow the page spec (2.2): tall >= 960 px high, medium >= 860,
   short below; the headline block height is computed here from the same
   numbers the CSS variables are set from, so the stage always knows what is
   left above the fold, the CTA under the stage included (it ends 16 px
   above the fold unless the stage is already at its smallest scale).

   No-overlap rule (owner via lead): Buffy never covers text, a card or the
   composer at any frame; checked every 50 ms by
   scratch/teacher-landing/hero-overlap.mjs. */

export const DESK = {
  kind: 'desk',
  W: 860,
  H: 272,
  radius: 28,
  // step 1: the course card (all 11 chapters) is read on the axis, then
  // slides left (pairX) as the SAT skills window arrives; one chip per
  // linked chapter (10) sits on its chapter's row and a straight connector
  // runs row to chip across the 50 px between them (the "Any curriculum"
  // section's look). 20 px rows: 11 of them fill the 272 px card.
  course: { x: 250, y: 0, w: 360, h: 272, bar: 40, rowTop: 46, rowH: 20, show: 11, pairX: 12 },
  skills: { x: 422, y: 0, w: 426, h: 272, bar: 40 },
  // step 2: the assessment results (one card on the axis)
  results: { x: 170, y: 12, w: 520, h: 248, bar: 44, rowTop: 56, rowH: 60 },
  // step 3: the example on the left, Buffy's explanation on the right (owner)
  question: { x: 112, y: 16, w: 378, h: 240 },
  explain: { x: 508, y: 16, w: 240, h: 240 },
  // step 4: the draft is on the axis alone; beside the adaptive-practice
  // card the pair is balanced on it
  // (the Assign button is centered on the draft's last row: h 230)
  draft: { x: 112, y: 20, w: 350, h: 230, soloX: 255 },
  adaptive: { x: 480, y: 74, w: 268, h: 122 },
  // step 5: progress over the last 4 weeks (one card on the axis)
  progress: { x: 170, y: 12, w: 520, h: 248, bar: 44, rowTop: 56, rowH: 60 },
  tile: { x: -24, y: -20, w: 908, h: 312 },
  dragW: 214,
  // the file card glides from the right into the drop zone
  cursorFrom: { x: 760, y: 212 },
  cursorFrom4: { x: 900, y: 230 },
};

/* Phone: one column. Step 1's connected chapters show each skill chip under
   its chapter (the section's narrow look); step 3 puts the example and the
   explanation side by side (stacked only under 360 px, WHY_STACKED). */
export const PHONE = {
  kind: 'phone',
  W: 343,
  // 224: the SAT skills list (all 10 linked chapters) fits with a wrap or two
  H: 224,
  radius: 22,
  // a 16 px row pitch: 12 px text never shares a line box with the next row
  // all 11 chapters (a 16 px pitch; 12 px text); then the SAT skills list,
  // all 10 linked chapters, one compact row each
  course: { x: 0, y: 0, w: 343, h: 210, bar: 30, rowTop: 30, rowH: 16, show: 11 },
  linked: { x: 0, y: 0, w: 343, h: 224, bar: 30, gap: 3 },
  results: { x: 0, y: 0, w: 343, h: 204, bar: 32, rowTop: 38, rowH: 54 },
  // step 3: the example LEFT, Buffy's explanation RIGHT, side by side
  // (owner); the example's half is the wider one here (it carries four
  // choices and the "Most picked" / "Correct" tags), 8 px apart
  question: { x: 0, y: 0, w: 178, h: 200 },
  explain: { x: 186, y: 0, w: 157, h: 200 },
  draft: { x: 0, y: 0, w: 343, h: 146 },
  adaptive: { x: 0, y: 156, w: 343, h: 54 },
  progress: { x: 0, y: 0, w: 343, h: 204, bar: 32, rowTop: 38, rowH: 54 },
  tile: { x: -12, y: -12, w: 367, h: 240 },
  dragW: 186,
  // inside the screen, under the zone's label
  cursorFrom: { x: 150, y: 186 },
  cursorFrom4: { x: 360, y: 160 },
};

/* Under 360 px wide the two halves of step 3 would fall under the 12 px text
   floor, so they stack: the example over the explanation, 10 px apart. */
const WHY_STACKED = {
  whyStack: true,
  question: { x: 0, y: 0, w: 343, h: 152 },
  explain: { x: 0, y: 162, w: 343, h: 54 },
};

/* Narrow phones (k < 0.9, under about 340 px): the 12 px text floor needs a
   17 px row pitch; the course card still shows all 11 chapters. */
export const PHONE_NARROW = {
  ...PHONE,
  ...WHY_STACKED,
  narrow: true,
  // the 12 px floor makes every line taller here: a taller artboard
  H: 264,
  course: { ...PHONE.course, h: 216, rowTop: 28, rowH: 17, show: 11 },
  linked: { ...PHONE.linked, h: 264, gap: 2 },
  explain: { ...WHY_STACKED.explain, h: 66 },
  cursorFrom: { x: 130, y: 176 },
};

/* The band under the artboard (stage px, never scaled): the chat. At the
   dock (beat C) it is Buffy's line over the composer; in beats A and B the
   whole chat group (the teacher's message, Buffy's reply, the composer) sits
   in the middle of the stage, scaled up (owner: "in the center and larger").
   Buffy stays beside the composer, never scaled. */
const BAND = {
  desk: {
    gap: 16, composerH: 64, font: 18, pad: 20, send: 40, capFont: 17, capLh: 24, capGap: 12, maxW: 720,
    bubbleFont: 16, bubbleLh: 24, bubblePadX: 18, bubblePadY: 11, bubbleGap: 10, chipW: 240, attachH: 44, centerScale: 1.25,
  },
  phone: {
    gap: 12, composerH: 56, font: 16, pad: 14, send: 36, capFont: 14, capLh: 19, capGap: 10,
    bubbleFont: 15, bubbleLh: 22, bubblePadX: 14, bubblePadY: 9, bubbleGap: 8, chipW: 214, attachH: 42, centerScale: 1,
  },
};
/* Buffy's longest line (storyScript STEPS), for the line count (plain
   estimate at 0.52 em a char). */
const LONGEST_LINE = STEPS.reduce((n, st) => Math.max(n, ...[st.buffy, st.after].filter(Boolean).map((x) => x.length)), 0);
// the teacher's longest question, for the message's line count
const LONGEST_ASK = STEPS.reduce((n, st) => Math.max(n, (st.teacher || '').length), 0);

/* Text above the stage: headline and sub line. The CTA sits UNDER the
   stage (owner, 2026-09-29: "the Set up my class should be underneath"):
   headline, sub, stage, progress row, CTA. The CTA is part of the stage
   budget (design review 2026-09-29 #2): its bottom stays CTA_MARGIN above
   the fold wherever the stage can shrink to make room. */
export const TEXT_TIERS = {
  tall: { navGap: 56, h1: 76, h1Lh: 1.04, h1Lines: 2, h1Track: -0.035, h1Sub: 24, sub: 20, subLh: 30, subLines: 1, subStage: 32, cta: 48, ctaGap: 20 },
  medium: { navGap: 40, h1: 68, h1Lh: 1.04, h1Lines: 2, h1Track: -0.035, h1Sub: 22, sub: 20, subLh: 30, subLines: 1, subStage: 28, cta: 48, ctaGap: 20 },
  short: { navGap: 28, h1: 48, h1Lh: 1.08, h1Lines: 1, h1Track: -0.03, h1Sub: 14, sub: 18, subLh: 27, subLines: 1, subStage: 20, cta: 44, ctaGap: 16 },
  phone: { navGap: 28, h1: 34, h1Lh: 1.08, h1Lines: 2, h1Track: -0.025, h1Sub: 12, sub: 16, subLh: 24, subLines: 2, subStage: 20, cta: 48, ctaGap: 20 },
};

const NAV_H = 64;
const CTA_MARGIN = 16;
/* The progress row under the scene: step name above the segments (centered
   at every width); phone keeps 44 px tap targets. */
const PROGRESS = { desk: { gap: 14, h: 52 }, phone: { gap: 10, h: 62 } };

/* The headline's line height in whole px (so the blocks above the stage have
   integer heights and the stage starts on a whole pixel). */
export const h1LinePx = (tt) => Math.round(tt.h1 * tt.h1Lh);

/* Everything above the stage (the CTA is below it). */
export const textBlockH = (tt) => Math.round(
  tt.navGap + h1LinePx(tt) * tt.h1Lines + tt.h1Sub + tt.subLh * tt.subLines + tt.subStage,
);

function textTierFor(vw, vh) {
  if (vw < 700) return 'phone';
  if (vh >= 960) return 'tall';
  if (vh >= 860) return 'medium';
  return 'short';
}

/* The text tier for this viewport: the headline never outgrows the column
   (9.4vw, qa-formatting #4), the sub wraps to 2 lines on narrow desktops, and
   very narrow phones (< 360) tighten the gaps (#49). Laptops under 700 px
   of viewport (1366 x 768 screens: 657 px) tighten the short tier's gaps so
   the CTA still fits above the fold with legible cards. */
function textFor(tierName, vw, vh) {
  const base = TEXT_TIERS[tierName];
  const tt = { ...base, h1: Math.min(base.h1, Math.floor(vw * 0.094 * 10) / 10) };
  if (tierName !== 'phone' && vw < 820) tt.subLines = 2;
  if (tierName === 'phone' && vw < 360) {
    tt.navGap = 16;
    tt.subStage = 16;
  }
  if (tierName === 'short' && vh < 700) {
    tt.navGap = 20;
    tt.h1Sub = 12;
    tt.subStage = 16;
  }
  return tt;
}

/* The smallest desktop artboard scale: below it the scene's 12 px text
   floor outgrows the cards (skill names would be cut). The CTA may then end
   closer to the fold than CTA_MARGIN (1366 x 657: about 6 px). */
const K_MIN = 0.8;

/* Buffy's pixel scale on a screen (owner report 2026-09-29, "glasses
   melting"): at Windows 125 % or 150 % display scaling a 2x or 3x Buffy is
   2.5 or 4.5 DEVICE px per art pixel, so rows alternate 2 and 3 px and his
   thin glasses lines warp. He is drawn at a whole number of device px per art
   pixel instead: dev = round(s x dpr). His CSS scale u = dev / dpr may be
   fractional; every Buffy length below uses u. */
export function buffyScale(s, dpr = 1) {
  const dev = Math.max(1, Math.round(s * dpr));
  return { dev, u: dev / dpr };
}

/* Buffy's opaque extent around his feet anchor (native cell columns): his
   widest poses reach 24 columns left and 23 right of column 32 (the catch
   with the file, and the mirrored connect at 22). The composer is kept
   BUFFY_GAP clear of that reach, so even the catch never comes closer than
   10 px (final formatting QA #16: it was 5 px). */
const BUFFY_LEFT = 24;
const BUFFY_RIGHT = 23;
const BUFFY_GAP = 10;
const BUFFY_TALL = 45; // his tallest pose above his feet, art px

/* His feet x beside a composer whose left edge is at `composerX` (stage px). */
export const homeXFor = (geo, composerX) => Math.round(composerX - BUFFY_GAP - BUFFY_RIGHT * geo.s);

/* The band for a stage: composer and caption rects in stage px. */
function bandFor(kind, stageW, u, vwAvail) {
  const B = BAND[kind];
  let cw;
  let left;
  let stage = stageW;
  if (kind === 'desk') {
    // the composer is centered on the page axis; Buffy stands outside it on
    // the left, and the same room is kept empty on the right
    const side = (BUFFY_LEFT + BUFFY_RIGHT) * u + BUFFY_GAP + 8;
    // room for the scaled-up chat in beats A and B (at least 1.15x)
    cw = Math.floor(Math.min(B.maxW, (vwAvail - 2 * side) / 1.15));
    stage = Math.max(stageW, Math.round((cw + 2 * side) / 2) * 2);
    left = Math.round((stage - cw) / 2);
  } else {
    // a phone: Buffy at the left edge, the composer fills the rest
    left = Math.round((BUFFY_LEFT + BUFFY_RIGHT) * u + BUFFY_GAP);
    cw = stage - left;
  }
  const capW = cw - 2 * B.pad;
  const perLine = Math.max(1, Math.floor(capW / (B.capFont * 0.52)));
  const needLines = Math.ceil(LONGEST_LINE / perLine);
  // Buffy (45 art px tall above his feet) stands beside the composer; the
  // line area above it is reserved up to his height
  const forBuffy = Math.ceil(((BUFFY_TALL * u) + 4 - B.composerH - B.capGap) / B.capLh);
  const capLines = Math.min(4, Math.max(needLines, forBuffy));
  const capH = capLines * B.capLh;
  const h = capH + B.capGap + B.composerH;
  const composer = {
    x: left, y: capH + B.capGap, w: cw, h: B.composerH, pad: B.pad, send: B.send, font: B.font,
    chipW: Math.min(B.chipW, cw - 2 * B.pad), attachH: B.attachH,
  };
  // the teacher's message: right-aligned over Buffy's reply; step 1's also
  // carries the file chip (30 px) on its own row
  const bubbleH = B.bubblePadY * 2 + B.bubbleLh;
  // the lines a typical question takes (1 on desktop, 2 on a phone), for
  // centering the chat group (plain estimate at 0.5 em a char)
  const askLines = Math.max(1, Math.ceil((LONGEST_ASK * B.bubbleFont * 0.5) / (Math.round(cw * 0.8) - 2 * B.bubblePadX)));
  const bubble = {
    // right above the lines his longest reply needs (not the lines kept
    // free for his height), so the message sits close to the conversation
    right: left + cw,
    bottom: capH - needLines * B.capLh - B.bubbleGap,
    h: bubbleH,
    hChip: bubbleH + 36,
    hAsk: bubbleH + (askLines - 1) * B.bubbleLh,
    font: B.bubbleFont, lh: B.bubbleLh, padX: B.bubblePadX, padY: B.bubblePadY, maxW: Math.round(cw * 0.8),
  };
  // the center scale: as large as the stage's width allows, up to 1.25
  const side = (BUFFY_LEFT + BUFFY_RIGHT) * u + BUFFY_GAP + 8;
  const sc = kind === 'desk' ? Math.max(1, Math.min(B.centerScale, (vwAvail - 2 * side) / cw)) : 1;
  return {
    stageW: stage,
    gap: B.gap,
    h,
    composer,
    caption: { x: left + B.pad, y: 0, w: capW, h: capH, lines: capLines, font: B.capFont, lh: B.capLh },
    bubble,
    sc,
  };
}

/* Everything the hero needs to size itself for a viewport (CSS px) and its
   devicePixelRatio. */
export function heroLayout(vw, vh, dpr = 1) {
  const tierName = textTierFor(vw, vh);
  const tt = textFor(tierName, vw, vh);
  let L = tierName === 'phone' ? PHONE : DESK;
  let k;
  let s;
  if (L.kind === 'phone') {
    k = Math.min(1.2, (vw - 32) / L.W);
    s = 2;
    if (k < 0.9) L = PHONE_NARROW;
    else if (vw < 360) L = { ...PHONE, ...WHY_STACKED };
  } else {
    const pr = PROGRESS.desk;
    const fit = (sGuess) => {
      const { u } = buffyScale(sGuess, dpr);
      const band = bandFor('desk', 0, u, vw - 32);
      // everything but the artboard: text above, the band, the progress
      // row and the CTA under the stage (its bottom CTA_MARGIN above the fold)
      const avail = vh - NAV_H - textBlockH(tt) - pr.gap - pr.h - band.gap - band.h - tt.ctaGap - tt.cta - CTA_MARGIN;
      return Math.min(1.16, (vw - 32) / L.W, avail / L.H);
    };
    k = fit(2);
    s = k >= 1 ? 3 : 2;
    if (s === 3) k = fit(3);
    k = Math.max(K_MIN, k);
  }
  return stageGeometry(L, k, s, tierName, tt, dpr, vw - 32);
}

/* Stage geometry for a given artboard, scale k, nominal Buffy scale s (2 or 3)
   and devicePixelRatio. Also used directly by the scene lab (fixed sizes). */
export function stageGeometry(L, k0, sNominal, tierName = L.kind === 'phone' ? 'phone' : 'medium', text = TEXT_TIERS[tierName], dpr = 1, vwAvail = null) {
  const { dev, u: s } = buffyScale(sNominal, dpr);
  const pr = PROGRESS[L.kind];
  // desktop: an even stage width keeps the centered stage (and Buffy) on
  // whole pixels (#43). Phone: the stage IS the content column (343 at 375),
  // never 1 px wider than it (final formatting QA #13)
  const artW = L.kind === 'phone' ? Math.floor(L.W * k0 + 1e-6) : Math.round((L.W * k0) / 2) * 2;
  const k = artW / L.W;
  const artH = Math.round(L.H * k);
  const band = bandFor(L.kind, artW, s, vwAvail ?? artW + 400);
  const stageW = band.stageW;
  const artX = Math.round((stageW - artW) / 2);
  const bandTop = artH + band.gap;
  const bandBottom = bandTop + band.h;
  // HOME: his feet on the composer's bottom line; his widest pose stops 12 px
  // short of the composer (#22)
  const homeX = Math.round(band.composer.x - BUFFY_GAP - BUFFY_RIGHT * s);
  const HOME = { x: homeX, y: bandBottom };
  // beats A and B: the chat group (the teacher's message, Buffy's reply
  // line, the composer), scaled by sc about the composer's bottom center, is
  // centered optically between the stage top and the step label: equal
  // space above the message and below the composer (final formatting QA
  // #11; it used to center step 1's taller file message, which left the
  // composer 60 to 80 px low). dy moves it up (stage px, a whole number).
  const c = band.composer;
  const origin = { x: c.x + c.w / 2, y: c.y + c.h }; // band coords
  const groupTop = band.bubble.bottom - band.bubble.hAsk;
  const groupH = origin.y - groupTop;
  const regionH = bandBottom + pr.gap; // stage top to the step label
  const centerY = Math.round(regionH / 2 + (groupH * band.sc) / 2); // the composer's bottom, stage px
  const chat = {
    origin,
    sc: band.sc,
    dy: centerY - (bandTop + origin.y),
  };
  return {
    L,
    kind: L.kind,
    tier: tierName,
    text,
    k,
    s, // CSS px per art pixel (dev / dpr)
    dev, // device px per art pixel (a whole number)
    dpr,
    artW,
    artH,
    artX,
    band: { ...band, top: bandTop, chat },
    stageW,
    stageH: bandBottom + pr.gap + pr.h,
    progress: { top: bandBottom + pr.gap, h: pr.h },
    HOME,
    LEDGE: HOME, // (no ledge: Buffy never leaves HOME)
    arcLift: { up: 0, down: 0 },
    lift: 0,
    corridor: null,
  };
}

/* Buffy's cell (top-left, stage px) for feet at (x, y), on the device-pixel
   grid: each art pixel is `dev` whole device pixels, and the cell starts on
   a whole device pixel, so with the stage origin snapped to the device grid
   (buffy/pixelSnap.js) he always lands on exact device pixels. The snap is
   one device pixel (not one art pixel, as before), so his feet sit on the
   composer's bottom line exactly (final formatting QA #15: they were 1 px
   off where that line fell between two art pixels). His feet are native
   row 55, so the cell top is 56 cells above them. */
export function cellAt(geo, x, y) {
  const { s } = geo;
  const dpr = geo.dpr || 1;
  const snap = (v) => Math.round(v * dpr) / dpr;
  return { left: snap(x - 32 * s), top: snap(y - 56 * s) };
}

/* A band point (dock coords) under the chat's transform at progress f (0 =
   dock, 1 = center): stage px. */
export function chatPoint(geo, p, f) {
  const { chat, top } = geo.band;
  const sc = 1 + (chat.sc - 1) * f;
  return {
    x: chat.origin.x + (p.x - chat.origin.x) * sc,
    y: top + chat.origin.y + (p.y - chat.origin.y) * sc + chat.dy * f,
  };
}

/* Buffy's feet beside the composer at chat progress f (stage px): his widest
   pose 12 px short of the composer's (scaled) left edge, feet on its bottom. */
export function homeAtChat(geo, f) {
  const c = geo.band.composer;
  const corner = chatPoint(geo, { x: c.x, y: c.y + c.h }, f);
  return { x: homeXFor(geo, corner.x), y: Math.round(corner.y) };
}

/* Stage px to ARTBOARD px (the art is centered in the stage and scaled by k). */
export const stageToArt = (geo, p) => ({ x: (p.x - (geo.artX || 0)) / geo.k, y: p.y / geo.k });

/* A native sprite anchor (top-left px + size) to ARTBOARD coords, for Buffy
   standing with feet at `at` (stage px). Used for the file's landing spot. */
export function anchorToArt(geo, at, rect) {
  const cell = cellAt(geo, at.x, at.y);
  const cx = cell.left + (rect.x + (rect.width || 0) / 2) * geo.s;
  const cy = cell.top + (rect.y + (rect.height || 0) / 2) * geo.s;
  return stageToArt(geo, { x: cx, y: cy });
}
